<template>
  <header class="app-header">
    <div class="header-content">
      <div class="logo">
        <PlMark :width="30" class="logo-mark" />
        <h1>PeaksLog</h1>
        <span class="beta-badge">Beta</span>
      </div>
      <nav class="main-nav">
        <router-link to="/summary" class="nav-link">Pulpit</router-link>
        <router-link to="/challenges" class="nav-link">Odznaczenia</router-link>
      </nav>
      <div class="user-section">
        <div class="user-menu">
          <button @click="toggleDropdown" class="user-menu-btn">
            <span class="user-icon">👤</span>
            <span class="user-email-short">{{ userEmailShort }}</span>
            <span class="dropdown-arrow" :class="{ open: showDropdown }">▼</span>
          </button>
          <div v-if="showDropdown" class="user-dropdown">
            <div class="user-info">
              <div class="user-email-full">{{ userEmail }}</div>
            </div>
            <div class="dropdown-divider"></div>
            <div v-if="authStore.isAdmin" class="admin-section">
              <button @click="goToAdminUsers" class="dropdown-item admin-btn">
                <span class="item-icon">👥</span>
                Manage Users
              </button>
              <div class="dropdown-divider"></div>
              <button @click="goToAdminPeaks" class="dropdown-item admin-btn">
                <span class="item-icon">⛰️</span>
                Szczyty
              </button>
              <div class="dropdown-divider"></div>
              <button @click="goToAdminMap" class="dropdown-item admin-btn">
                <span class="item-icon">🗺️</span>
                View Admin Map
              </button>
              <div class="dropdown-divider"></div>
              <button @click="goToAdminChallenges" class="dropdown-item admin-btn">
                <span class="item-icon">⚙️</span>
                Manage Challenges
              </button>
              <div class="dropdown-divider"></div>
            </div>
            <button @click="deleteAllData" class="dropdown-item delete-btn">
              <span class="item-icon">🗑️</span>
              Delete All Data
            </button>
            <div class="dropdown-divider"></div>
            <button @click="logout" class="dropdown-item logout-btn">
              <span class="item-icon">🚪</span>
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import PlMark from '@/components/pl/PlMark.vue'
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'
import api from '@/services/api'

const authStore = useAuthStore()
const router = useRouter()

const showDropdown = ref(false)

const userEmail = computed(() => authStore.user?.email || '')
const userEmailShort = computed(() => {
  const email = userEmail.value
  if (!email) return ''
  const [username] = email.split('@')
  return username.length > 10 ? username.substring(0, 10) + '...' : username
})

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value
}

const closeDropdown = () => {
  showDropdown.value = false
}

const logout = () => {
  authStore.logout()
  router.push('/login')
  closeDropdown()
}

const goToAdminUsers = () => {
  router.push('/admin/users')
  closeDropdown()
}

const goToAdminPeaks = () => {
  router.push('/admin/peaks')
  closeDropdown()
}

const goToAdminMap = () => {
  router.push('/admin/map')
  closeDropdown()
}

const goToAdminChallenges = () => {
  router.push('/admin/challenges')
  closeDropdown()
}

const deleteAllData = async () => {
  if (confirm('Are you sure you want to delete all your data? This action cannot be undone.')) {
    try {
      await api.delete('/api/user')
      alert('All data deleted successfully. You will be logged out.')
      authStore.logout()
      router.push('/login')
      closeDropdown()
    } catch (error) {
      console.error('Error deleting data:', error)
      alert('Error deleting data. Please try again.')
    }
  }
}

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
  const userMenu = event.target.closest('.user-menu')
  if (!userMenu) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.app-header {
  background: linear-gradient(135deg, var(--primary-gradient-start) 0%, var(--primary-gradient-end) 100%);
  color: white;
  padding: 0;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.logo-mark {
  color: white;
}

.logo h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.beta-badge {
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.main-nav {
  display: flex;
  gap: 2rem;
}

.nav-link {
  color: white;
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: background-color 0.3s ease;
  font-weight: 500;
}

.nav-link:hover,
.nav-link.router-link-active {
  background-color: rgba(255, 255, 255, 0.2);
}

.user-section {
  display: flex;
  align-items: center;
}

.user-menu {
  position: relative;
}

.user-menu-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  font-size: 0.9rem;
}

.user-menu-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.user-icon {
  font-size: 1.1rem;
}

.user-email-short {
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-arrow {
  font-size: 0.8rem;
  transition: transform 0.3s ease;
}

.dropdown-arrow.open {
  transform: rotate(180deg);
}

.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--bg-white);
  color: var(--text-primary);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 1000;
  margin-top: 0.5rem;
  overflow: hidden;
}

.user-info {
  padding: 1rem;
  background: var(--bg-light);
  border-bottom: 1px solid var(--bg-light-accent);
}

.user-email-full {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-secondary);
}

.dropdown-divider {
  height: 1px;
  background: var(--bg-light-accent);
  margin: 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.dropdown-item:hover {
  background: var(--bg-light);
}

.dropdown-item.delete-btn:hover {
  background: var(--bg-success);
  color: var(--error-color);
}

.dropdown-item.logout-btn:hover {
  background: var(--bg-light-accent);
  color: var(--text-primary);
}

.dropdown-item.admin-btn {
  color: var(--primary-color);
  font-weight: 500;
}

.dropdown-item.admin-btn:hover {
  background: var(--bg-primary-light);
  color: var(--primary-color);
}

.item-icon {
  font-size: 1rem;
}

@media (max-width: 768px) {
  .header-content {
    padding: 0.75rem 1rem;
    gap: 0.5rem;
  }

  .logo h1 {
    font-size: 1.2rem;
  }

  .subtitle {
    font-size: 0.7rem;
  }

  .main-nav {
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    gap: 0.5rem;
    padding: 0.25rem 0;
  }

  .main-nav::-webkit-scrollbar {
    height: 4px;
  }

  .main-nav::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }

  .main-nav::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
  }

  .nav-link {
    padding: 0.4rem 0.75rem;
    font-size: 0.85rem;
    white-space: nowrap;
  }

  .user-menu-btn {
    padding: 0.4rem 0.75rem;
    font-size: 0.85rem;
  }

  .user-icon {
    font-size: 1rem;
  }

  /* Limit email display to ~6 characters to prevent overflow on small tablets */
  .user-email-short {
    max-width: 6ch;
  }
}

@media (max-width: 480px) {
  .header-content {
    padding: 0.5rem 0.75rem;
  }

  .logo h1 {
    font-size: 1rem;
  }

  .beta-badge {
    display: none;
  }

  .nav-link {
    padding: 0.35rem 0.6rem;
    font-size: 0.8rem;
  }

  .user-menu-btn {
    padding: 0.35rem 0.6rem;
    font-size: 0.8rem;
  }

  .user-email-short {
    display: none;
  }
}
</style>